import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  BanknotesIcon, ArrowTopRightOnSquareIcon, PlusIcon, MinusCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

// Reusable Points Wallet card. Shows current balance + cashout CTA
// (staff-side) OR balance + Adjust Points button (admin viewing
// another staff). Also shows a probation-warning banner if the
// balance is under the configured threshold.

const dollars = (cents) => `$${(Number(cents) / 100).toFixed(2)}`;

export default function WalletBalanceCard({ userId }) {
  const { user, isDirector, isManagement } = useAuth();
  const isSelf = user?.id === userId;
  const canManage = !isSelf && isManagement;
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/wallet/${userId}/balance`)
      .then(({ data }) => setState(data))
      .catch(() => toast.error('Could not load wallet'))
      .finally(() => setLoading(false));
    api.get(`/wallet/${userId}/events`)
      .then(({ data }) => setEvents(data))
      .catch(() => {});
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const reverse = async (event) => {
    const amt = event.points > 0 ? `+${event.points}` : `${event.points}`;
    if (!window.confirm(
      `Reverse this event? (${amt} pts — ${event.reason || event.event_type})\n\n` +
      'The original stays in history for the audit trail. A matching offsetting event will be added under your name.'
    )) return;
    try {
      await api.post(`/wallet/events/${event.id}/reverse`);
      toast.success('Event reversed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reverse');
    }
  };

  const requestCashout = async () => {
    if (!window.confirm(
      `Request a cashout of ${state.config.cashout_min_points} points for ${dollars(state.config.cashout_cash_cents)}?\n\n` +
      `You can't stack cashouts — you'll need to wait for this one to be approved before making another.`
    )) return;
    setBusy(true);
    try {
      await api.post('/wallet/me/cashout');
      toast.success('Cashout requested. Admin will review.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cashout failed');
    } finally { setBusy(false); }
  };

  if (loading || !state) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm animate-pulse">
        <div className="h-4 w-24 bg-gray-100 rounded mb-4" />
        <div className="h-10 w-32 bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <BanknotesIcon className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-900">Points Wallet</h3>
        </div>
        <button onClick={() => setShowLedger(!showLedger)}
          className="text-xs font-medium text-gray-500 hover:text-gray-700">
          {showLedger ? 'Hide history' : 'Show history'}
        </button>
      </div>

      <div className="flex items-baseline gap-3">
        <span className="text-4xl font-bold text-gray-900 tabular-nums">{state.balance}</span>
        <span className="text-sm text-gray-500">points</span>
      </div>

      {state.probation && (
        <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Below the probation threshold ({state.config.probation_threshold}).
            Attendance discipline may apply.
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {isSelf && (
          <button onClick={requestCashout}
            disabled={busy || !state.cashout_ready}
            title={!state.cashout_ready
              ? `You need ${state.config.cashout_min_points} points to cash out.`
              : ''}
            className="text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed bg-orange-500 hover:bg-orange-600 text-white">
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            {state.cashout_ready
              ? `Cash Out (${state.config.cashout_min_points} pts → ${dollars(state.config.cashout_cash_cents)})`
              : `Cash Out at ${state.config.cashout_min_points} pts`}
          </button>
        )}
        {canManage && (
          <button onClick={() => setAdjustOpen(true)}
            className="text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700">
            <PlusIcon className="w-4 h-4" /> Adjust Points
          </button>
        )}
      </div>

      {showLedger && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <h4 className="text-[11px] font-semibold uppercase text-gray-500 tracking-wide mb-2">
            History
          </h4>
          {events.length === 0 ? (
            <p className="text-xs text-gray-400">No events yet.</p>
          ) : (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto">
              {events.map(e => {
                const isReversed = !!e.reversed_at;
                const isReversalRow = typeof e.source_ref === 'string' && e.source_ref.startsWith('reversal:');
                return (
                  <li key={e.id} className={`flex items-start justify-between text-xs gap-3 ${isReversed ? 'opacity-50' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <p className={`truncate ${isReversed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {e.reason || labelFor(e.event_type)}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {new Date(e.created_at).toLocaleString()}
                        {e.created_by_name && ` · ${e.created_by_name}`}
                        {isReversed && e.reversed_by_name && (
                          <> · <span className="text-red-600">reversed by {e.reversed_by_name}</span></>
                        )}
                        {isReversalRow && <> · <span className="text-red-600">reversal</span></>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`font-mono tabular-nums font-semibold ${e.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {e.points > 0 ? '+' : ''}{e.points}
                      </span>
                      {isDirector && !isReversed && !isReversalRow && e.event_type !== 'redemption' && (
                        <button onClick={() => reverse(e)}
                          title="Reverse this point event. Original stays in history."
                          className="text-[10px] text-red-600 hover:text-red-700 hover:underline">
                          Reverse
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {adjustOpen && (
        <AdjustModal userId={userId}
          onClose={() => setAdjustOpen(false)}
          onSaved={() => { setAdjustOpen(false); load(); }} />
      )}
    </div>
  );
}

function labelFor(t) {
  return ({
    hire_bonus: 'Hire bonus',
    manual_adjustment: 'Adjustment',
    monthly_perfect: 'Perfect month bonus',
    redemption: 'Cashout',
    late_arrival: 'Late arrival',
    late_break: 'Late back from break',
    call_off: 'Call off',
    call_off_doctor: 'Call off (doctor note)',
  })[t] || t;
}

function AdjustModal({ userId, onClose, onSaved }) {
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(points, 10);
    if (!Number.isFinite(n) || n === 0) return toast.error('Enter a non-zero number of points.');
    if (!reason.trim()) return toast.error('Reason is required.');
    setBusy(true);
    try {
      await api.post(`/wallet/${userId}/adjust`, { points: n, reason: reason.trim() });
      toast.success('Adjusted');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Adjust failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2 mb-3">
          <MinusCircleIcon className="w-5 h-5 text-orange-500" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">Adjust Points</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Positive to add, negative to subtract. Use to backfill starting balances or correct mistakes.
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Points (+/-)</label>
            <input value={points} onChange={(e) => setPoints(e.target.value)}
              type="number" placeholder="e.g. 25 or -5"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-1">Reason</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Backfill starting balance, kudos, correction"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button type="submit" disabled={busy}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {busy ? 'Saving…' : 'Save adjustment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
